import { Outlet, createFileRoute, redirect, Link, useNavigate } from '@tanstack/react-router';
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { useAuth } from '../auth/useAuth';
import { CompanySwitcher } from '../components/shell/CompanySwitcher';
import { ThemeToggle } from '../components/shell/ThemeToggle';
import { UpdateBanner } from '../components/shell/UpdateBanner';
import { ConsentGate } from '../components/consent/ConsentGate';
import { useSyncAllPlaid } from '../hooks/useMePlaid';
import { useNudges } from '../hooks/useNudges';
import { BILLING_COMPANY_ID } from '@accounting/shared';
import { useActiveCompanyId } from '../stores/useCompanyStore';

export const Route = createFileRoute('/_auth')({
  beforeLoad: ({ context, location }) => {
    if (context.msalInstance.getAllAccounts().length === 0) {
      throw redirect({
        to: '/login',
        search: { redirect: location.href },
      });
    }
  },
  component: AuthLayout,
});

// Pinned in the sidebar (kept lean by request); everything else lives in the
// header dropdown menus below.
//
// Command Center is not listed: the brand at the top of the sidebar links to
// it, so a second entry pointing at the same page was pure duplication.
// `Bank` (the account-admin list) is gone too — everything it offered is
// reachable from Plaid accounts, which is pinned lower down. `Companies` moved
// under Settings; it is configuration, not daily work.
//
// `nudge` names the count that renders as a badge. A number here means "this
// many things are waiting for you" — see useNudges.
const SIDEBAR_ITEMS = [
  { to: '/books-health', label: 'Books Health', exact: false, nudge: 'booksHealth' },
  // Daily work: the ledger itself, and the accounts it posts to.
  { to: '/journal', label: 'Journal Entries', exact: false, nudge: null },
  { to: '/accounts', label: 'Accounts', exact: false, nudge: null },
  { to: '/bank/overview', label: 'Bank Transactions', exact: false, nudge: 'bankUnmatched' },
  // Rules get their own entry rather than living behind the bank page. Pruning
  // stale rules is ongoing work, not a one-time setup step.
  { to: '/bank/rules', label: 'Bank Rules', exact: false, nudge: null },
  { to: '/reconcile', label: 'Reconcile', exact: false, nudge: 'reconcile' },
  { to: '/payroll', label: 'Payroll', exact: false, nudge: null },
  { to: '/budget', label: 'Budget', exact: false, nudge: null },
  { to: '/reports', label: 'Reports', exact: false, nudge: null },
  { to: '/documents', label: 'Documents', exact: false, nudge: null },
  // Pinned for its badge. Hours confirm themselves, so the only reason to come
  // here unprompted is a visit that could not be paired, and the badge is the
  // only thing that says there is one.
  { to: '/participation', label: 'Participation', exact: false, nudge: 'participationExceptions' },
  // Last, and pinned rather than buried under Transactions: a queued check is
  // an unpaid bill sitting in a drawer, and the badge is the only thing that
  // says how many are waiting.
  { to: '/checks/print-queue', label: 'Print Queue', exact: false, nudge: 'checksToPrint' },
] as const;

// Grouped sidebar menus (each is a named entry that opens a flyout on hover).
// `Journal` and `Payroll` are deliberately absent — both are pinned in the
// sidebar above, and two entries pointing at the same route is exactly the
// duplication we removed.
const TRANSACTIONS_LINKS = [
  { to: '/recurring', label: 'Recurring' },
  { to: '/checks', label: 'Checks' },
  { to: '/deposits', label: 'Deposits' },
  { to: '/invoices', label: 'Invoices' },
  { to: '/bills', label: 'Bills' },
] as const;

const ASSETS_LINKS = [
  { to: '/fixed-assets', label: 'Fixed Assets' },
  { to: '/loans', label: 'Loans' },
  { to: '/shareholder-loans', label: 'Shareholder Loans' },
  { to: '/intercompany', label: 'Intercompany' },
] as const;

const MORE_LINKS = [
  { to: '/report-builder', label: 'Report Builder' },
  { to: '/year-end', label: 'Year-End' },
  { to: '/reallocations', label: 'Reallocations' },
  { to: '/audit-log', label: 'Audit Log' },
  { to: '/locations', label: 'Locations' },
  { to: '/classes', label: 'Classes' },
  { to: '/companies', label: 'Companies' },
] as const;

// The personal assistant: not company-scoped, so it lives in its own group.
// Today / Attention / Tasks join this list as their phases ship.
const ASSISTANT_LINKS = [
  { to: '/assistant/today', label: 'Today' },
  { to: '/assistant/calendar', label: 'Calendar' },
  { to: '/assistant/attention', label: 'Email' },
  { to: '/assistant/tasks', label: 'Tasks' },
  { to: '/assistant/connections', label: 'Connections' },
] as const;

const PEOPLE_LINKS = [
  { to: '/customers', label: 'Customers' },
  { to: '/employees', label: 'Employees' },
  { to: '/vendors', label: 'Vendors' },
  { to: '/vendor-center', label: 'Vendor Center' },
  { to: '/customer-center', label: 'Customer Center' },
  { to: '/statements', label: 'Statements' },
] as const;

function AuthLayout() {
  const { currentUser, logout } = useAuth();
  return (
    <ConsentGate>
      <_AuthLayoutInner currentUser={currentUser} logout={logout} />
    </ConsentGate>
  );
}

function _AuthLayoutInner({ currentUser, logout }: { currentUser: ReturnType<typeof useAuth>['currentUser']; logout: ReturnType<typeof useAuth>['logout'] }) {
  const nudges = useNudges();
  // Bateman Capital Management gets two coloured shortcuts at the top of the
  // sidebar. Invoices is already under Transactions and stays there — this is a
  // second door to the same room, for the company that lives in it. EOM
  // Financials exists only here.
  const activeCompanyId = useActiveCompanyId();
  const isBcm = activeCompanyId === BILLING_COMPANY_ID;
  return (
    <div className="app-shell">
      <aside className="sidebar">
        <Link to="/" className="sidebar-brand" title="Command Center — your cross-company home">
          Bateman
        </Link>
        <nav style={{ display: 'flex', flexDirection: 'column', gap: 2, flex: 1 }}>
          {isBcm && (
            <>
              <Link
                to="/invoices"
                className="nav-link nav-link--pinned nav-link--invoices"
                title="Advisory fee invoices — also under Transactions"
              >
                <span>Invoices</span>
              </Link>
              <Link
                to="/eom-financials"
                className="nav-link nav-link--pinned nav-link--eom"
                title="Assemble the month-end regulatory packet"
              >
                <span>EOM Financials</span>
              </Link>
            </>
          )}
          {SIDEBAR_ITEMS.map((item) => {
            const count = item.nudge ? nudges[item.nudge] : 0;
            return (
              <Link
                key={item.to}
                to={item.to}
                className="nav-link"
                activeOptions={item.exact ? { exact: true } : undefined}
              >
                <span>{item.label}</span>
                {count > 0 && (
                  <span
                    className="nav-badge"
                    data-severity={
                      item.nudge === 'booksHealth' && nudges.hasFailure ? 'fail' : 'warn'
                    }
                    aria-label={`${count} ${count === 1 ? 'item needs' : 'items need'} attention`}
                  >
                    {count > 99 ? '99+' : count}
                  </span>
                )}
              </Link>
            );
          })}
          {/* Grouped menus. These four used to be unlabeled icons in the topbar,
              where the owner could not find what was behind them. */}
          <SidebarMenu label="Transactions" icon={<TxIcon />} links={TRANSACTIONS_LINKS} />
          <SidebarMenu label="Assets & Loans" icon={<AssetIcon />} links={ASSETS_LINKS} />
          <SidebarMenu label="People" icon={<PeopleIcon />} links={PEOPLE_LINKS} />
          <SidebarMenu label="Setup & Tools" icon={<MoreIcon />} links={MORE_LINKS} />
          <SidebarMenu
            label="Assistant"
            icon={<AssistantIcon />}
            links={ASSISTANT_LINKS}
            badge={nudges.assistantReauth + nudges.assistantAttention}
            badgeTitle="items waiting in the assistant"
          />

          {/* Pinned to the bottom of the sidebar with icons, by request. */}
          <SyncAllPlaidButton />
          <Link
            to="/plaid"
            className="nav-link"
            style={{ display: 'flex', alignItems: 'center', gap: 8 }}
            title="Map every Plaid account across all your companies in one place."
          >
            <PlaidIcon />
            Plaid accounts
          </Link>
          <Link
            to="/ask-ai"
            className="nav-link"
            style={{ display: 'flex', alignItems: 'center', gap: 8 }}
          >
            <AiIcon />
            Ask AI
          </Link>
        </nav>
        {/* Theme and Sign out live in the top header — the sidebar is for
            navigation, not account controls. */}
      </aside>

      <div className="main">
        <header className="topbar">
          <CompanySwitcher />
          <TopbarSearch />
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginLeft: 'auto' }}>
            <Link to="/import" aria-label="Import" title="Import from QuickBooks" className="icon-link">⤒</Link>
            <Link to="/settings" aria-label="Settings" title="Settings" className="icon-link">⚙</Link>
            <ThemeToggle />
            <span style={{ color: 'var(--text-muted)', fontSize: 14, marginLeft: 6 }}>
              {currentUser?.email}
            </span>
            <button className="btn-secondary" onClick={() => void logout()} style={{ marginLeft: 4 }}>
              Sign out
            </button>
          </div>
        </header>
        <main className="main-content">
          <UpdateBanner />
          <Outlet />
        </main>
      </div>
    </div>
  );
}

/**
 * A named sidebar entry that reveals its links in a flyout on hover.
 *
 * The flyout is `position: fixed`, not absolutely positioned inside the
 * sidebar: `.sidebar` sets `overflow-y: auto`, and a box that scrolls on one
 * axis clips the other, so a `left: 100%` panel would be sliced off at the
 * sidebar's edge. Measuring the trigger and painting the panel in viewport
 * coordinates sidesteps the clip entirely.
 */
function SidebarMenu({
  label,
  icon,
  links,
  badge = 0,
  badgeTitle,
}: {
  label: string;
  icon: ReactNode;
  links: readonly { to: string; label: string }[];
  /** A count that renders next to the label, like the pinned items' badges. */
  badge?: number;
  badgeTitle?: string;
}) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  const place = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    // Roughly 40px per row plus the panel's padding, so a long menu opening
    // near the bottom of the screen gets pushed up instead of off-screen.
    const height = links.length * 40 + 12;
    const top = Math.max(8, Math.min(r.top, window.innerHeight - height - 8));
    // Flush against the trigger, no gap: a dead zone between the two would fire
    // mouseleave as the pointer crosses it and the panel would vanish mid-reach.
    setPos({ top, left: r.right });
  }, [links.length]);

  const show = useCallback(() => {
    place();
    setOpen(true);
  }, [place]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    // The panel is fixed, so it would otherwise hang in place while the
    // sidebar scrolls out from under it.
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open, place]);

  return (
    <div
      ref={ref}
      className="sidebar-menu-wrap"
      onMouseEnter={show}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        type="button"
        className="nav-link sidebar-menu-trigger"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => (open ? setOpen(false) : show())}
        onFocus={show}
      >
        <span className="sidebar-menu-icon">{icon}</span>
        <span className="sidebar-menu-label">{label}</span>
        {badge > 0 && (
          <span className="nav-badge" data-severity="warn" aria-label={`${badge} ${badgeTitle ?? 'items need attention'}`}>
            {badge > 99 ? '99+' : badge}
          </span>
        )}
        <span className="sidebar-menu-caret" aria-hidden="true">
          ›
        </span>
      </button>
      {open && pos && (
        <div
          className="sidebar-flyout"
          role="menu"
          aria-label={label}
          style={{ top: pos.top, left: pos.left }}
        >
          {links.map((l) => (
            <Link key={l.to} to={l.to} role="menuitem" onClick={() => setOpen(false)}>
              {l.label}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

function AssistantIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <rect x="3" y="5" width="18" height="16" rx="2" />
      <path d="M3 10h18M8 3v4M16 3v4" />
      <path d="M8 15h3M13 15h3" />
    </svg>
  );
}

function PeopleIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <circle cx="12" cy="8" r="4" />
      <path d="M4 20c0-4.4 3.6-6 8-6s8 1.6 8 6v1H4z" />
    </svg>
  );
}

function TxIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path d="M4 7h13M4 7l3-3M4 7l3 3" />
      <path d="M20 17H7M20 17l-3-3M20 17l-3 3" />
    </svg>
  );
}

function AssetIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path d="M3 10l9-6 9 6" />
      <path d="M5 10v9h14v-9" />
      <path d="M9 19v-5h6v5" />
    </svg>
  );
}

function MoreIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <circle cx="5" cy="12" r="2" />
      <circle cx="12" cy="12" r="2" />
      <circle cx="19" cy="12" r="2" />
    </svg>
  );
}

// Sidebar button: sync every active Plaid Item across every company in one
// shot. Sits just above the Plaid accounts link.
function SyncAllPlaidButton() {
  const sync = useSyncAllPlaid();
  const [lastMsg, setLastMsg] = useState<string | null>(null);
  const onClick = (): void => {
    if (sync.isPending) return;
    sync.mutate(undefined, {
      onSuccess: (r) => {
        const parts: string[] = [];
        parts.push(`${r.itemsSucceeded} item${r.itemsSucceeded === 1 ? '' : 's'} synced`);
        if (r.added) parts.push(`${r.added} new`);
        if (r.itemsFailed) parts.push(`${r.itemsFailed} failed`);
        setLastMsg(parts.join(' · '));
      },
      onError: (e) => setLastMsg(`Sync failed: ${(e as Error).message}`),
    });
  };
  return (
    <div style={{ marginTop: 'auto', display: 'flex', flexDirection: 'column', gap: 2 }}>
      <button
        type="button"
        className="nav-link"
        onClick={onClick}
        disabled={sync.isPending}
        title="Pull new transactions from every Plaid-linked bank login across all your companies."
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          textAlign: 'left',
          background: 'transparent',
          border: 'none',
          cursor: sync.isPending ? 'progress' : 'pointer',
          width: '100%',
          font: 'inherit',
          color: 'var(--sidebar-text)',
        }}
      >
        <RefreshIcon spinning={sync.isPending} />
        {sync.isPending ? 'Syncing all…' : 'Sync all'}
      </button>
      {lastMsg && (
        <div
          style={{
            fontSize: 11,
            color: 'var(--text-muted)',
            padding: '0 12px 4px',
            lineHeight: 1.3,
          }}
        >
          {lastMsg}
        </div>
      )}
    </div>
  );
}

function RefreshIcon({ spinning }: { spinning: boolean }) {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      style={spinning ? { animation: 'spin 1s linear infinite' } : undefined}
    >
      <polyline points="23 4 23 10 17 10" />
      <polyline points="1 20 1 14 7 14" />
      <path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15" />
    </svg>
  );
}

// Bank/columns — used for the "Plaid accounts" sidebar entry.
function PlaidIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path d="M3 10l9-6 9 6" />
      <path d="M5 10v10h14V10" />
      <path d="M9 14v6M15 14v6" />
    </svg>
  );
}

// Sparkles — used for the "Ask AI" sidebar entry.
function AiIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M12 2l1.8 4.7L18.5 8.5l-4.7 1.8L12 15l-1.8-4.7L5.5 8.5l4.7-1.8L12 2z" />
      <path d="M19 14l.9 2.3L22 17.5l-2.1.9L19 21l-.9-2.6L16 17.5l2.1-1.2L19 14z" />
    </svg>
  );
}

function TopbarSearch() {
  const navigate = useNavigate();
  const [term, setTerm] = useState('');
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (term.trim()) void navigate({ to: '/search', search: { q: term.trim() } });
      }}
      style={{ flex: 1, maxWidth: 360, margin: '0 8px' }}
    >
      <input
        id="topbar-search"
        name="topbar-search"
        type="search"
        value={term}
        onChange={(e) => setTerm(e.target.value)}
        placeholder="Search…"
        aria-label="Search this company"
        autoComplete="off"
        style={{ width: '100%' }}
      />
    </form>
  );
}
